import { type ReactElement, Suspense } from "react"

import ReactFlow, { Background, type Edge, type Node, ReactFlowProvider, useStoreApi } from "reactflow"

import { Grid } from "@mui/material"

import { LoadingContainer } from "../../../components/core/LoadingContainer"
import { type ComponentTestingCanvasProps, type ComponentTestingEdge, edgeTypes, nodeTypes } from "../Interfaces"
import { getComponentTypeFromNode } from "../helpers"

import styles from "./ComponentTesting.module.scss"

export const ComponentTestingCanvas = ({
  selectedComponent,
  setSelectedComponent,
}: ComponentTestingCanvasProps): ReactElement => {
  const store = useStoreApi()
  const { edges, getNodes } = store.getState()
  const nodes = getNodes()

  const NODE_HEIGHT = 50
  const VERTICAL_SPACING = 40
  const HORIZONTAL_SPACING = 250
  const START_X = 100
  const START_Y = 50

  const nodesByLevel = nodes.reduce((acc: { [key: number]: Node[] }, node) => {
    const level = Math.round(node.position.y / 100)
    if (!acc[level]) acc[level] = []
    acc[level].push(node)
    return acc
  }, {})

  const sortedLevels = Object.keys(nodesByLevel)
    .map(Number)
    .sort((a, b) => a - b)

  const positionedNodes = sortedLevels.flatMap((level, levelIndex) => {
    const levelNodes = nodesByLevel[level]
    if (!levelNodes) return []

    const y = START_Y + levelIndex * (NODE_HEIGHT + VERTICAL_SPACING)

    // Center nodes horizontally within each level
    const totalWidth = levelNodes.length * HORIZONTAL_SPACING
    const startX = START_X - totalWidth / 2

    return levelNodes.map((node, nodeIndex) => {
      const enhancedNode = {
        ...node,
        position: {
          x: startX + nodeIndex * HORIZONTAL_SPACING,
          y: y,
        },
        data: {
          ...node.data,
          isSelected: selectedComponent?.id === node.id,
          selectedId: selectedComponent?.id,
        },
      }
      return enhancedNode
    })
  })

  const typedEdges = edges.map((edge: ComponentTestingEdge) => {
    // Check if edge should use custom label
    const hasCustomLabel = edge?.data?.text || edge?.data?.useCustomLabel || edge?.data?.labelType

    return {
      ...edge,
      type: (() => {
        if (edge.type === "custom" || hasCustomLabel) return "customLabel"

        return edge?.data?.edgeType ?? edge.type ?? "smoothstep"
      })(),

      label: edge?.data?.text || edge.label || "",

      ...(hasCustomLabel
        ? {}
        : {
            labelStyle: {
              fontSize: "8px",
              fontWeight: "500",
              color: "white",
            },
            labelBgStyle: {
              fill: "var(--base-background-2)",
              fillOpacity: 0.9,
              stroke: "var(--base-border-1)",
              strokeWidth: 1,
              textAnchor: "middle",
            },
            labelBgPadding: [2, 6],
            labelBgBorderRadius: 12,
          }),

      data: {
        ...edge.data,
        labelType: edge.data?.labelType || "default",
      },
    }
  })

  const onNodeClick = (_: React.MouseEvent, node: Node): void => {
    const type = getComponentTypeFromNode(node)
    if (!type) return
    if (node !== undefined && setSelectedComponent) {
      setSelectedComponent({
        id: node.id,
        type: type,
        data: node.data[type],
      })
    }
  }

  const onPaneClick = (): void => {
    if (setSelectedComponent) {
      setSelectedComponent(null)
    }
  }

  // Workaround to handle marketplace script nodes till we have a proper node type
  const formattedNodes = positionedNodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      nodeType: node.data.scriptType === "marketplace" ? "MarketPlaceScriptNode" : node.data.nodeType,
    },
  }))

  return (
    <Grid className={styles.canvasContainer}>
      <ReactFlowProvider>
        <Suspense fallback={<LoadingContainer />}>
          <ReactFlow
            nodes={formattedNodes}
            edges={typedEdges as Edge[]}
            edgeTypes={edgeTypes}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{
              type: "default",
            }}
            nodesDraggable={false}
            nodesConnectable={false}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
          >
            <Background />
          </ReactFlow>
        </Suspense>
      </ReactFlowProvider>
    </Grid>
  )
}
